import type {
    ContractDataComplexDTO,
    CreditPaymentDataResponse,
    CreditPaymentPlanDTO,
    PaymentData,
    PaymentPlan,
} from '@leanpay/models'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

interface AppState {
    isAuthenticated: boolean
    isExiting: boolean
    // Carried through the RequestOtp → Login hop so the recipient guard can branch.
    upsellFlow: boolean
    vendorToken: string
    phoneNumber: string | null
    progress: number
    isCookieConsentEnabled: boolean
    loanAmount: number | null
    installmentCount: number | null
    insuranceEnabled: boolean
    selectedPaymentPlan: CreditPaymentPlanDTO | null
    paymentData: CreditPaymentDataResponse | null
    contractData: ContractDataComplexDTO[] | null
    // SLO GPL (MPL) terms screen — kept separate from ROM's selectedPaymentPlan / paymentData.
    selectedMplPlan: PaymentPlan | null
    mplPaymentData: PaymentData | null

    setIsAuthenticated: (value: boolean) => void
    setIsExiting: (value: boolean) => void
    setUpsellFlow: (value: boolean) => void
    setVendorToken: (token: string) => void
    setPhoneNumber: (phone: string | null) => void
    setProgress: (progress: number) => void
    setIsCookieConsentEnabled: (value: boolean) => void
    setLoanAmount: (amount: number | null) => void
    setInstallmentCount: (count: number | null) => void
    setInsuranceEnabled: (value: boolean) => void
    setSelectedPaymentPlan: (plan: CreditPaymentPlanDTO | null) => void
    setPaymentData: (data: CreditPaymentDataResponse | null) => void
    setContractData: (data: ContractDataComplexDTO[] | null) => void
    setSelectedMplPlan: (plan: PaymentPlan | null) => void
    setMplPaymentData: (data: PaymentData | null) => void
    resetStore: () => void
}

export const useAppStore = create<AppState>()(
    persist(
        (set) => ({
            isAuthenticated: false,
            isExiting: false,
            upsellFlow: false,
            vendorToken: '',
            phoneNumber: null,
            progress: 0,
            isCookieConsentEnabled: false,
            loanAmount: null,
            installmentCount: null,
            insuranceEnabled: false,
            selectedPaymentPlan: null,
            paymentData: null,
            contractData: null,
            selectedMplPlan: null,
            mplPaymentData: null,

            setIsAuthenticated: (value) => set({ isAuthenticated: value }),
            setIsExiting: (value) => set({ isExiting: value }),
            setUpsellFlow: (value) => set({ upsellFlow: value }),
            setVendorToken: (token) => set({ vendorToken: token }),
            setPhoneNumber: (phone) => set({ phoneNumber: phone }),
            setProgress: (progress) => set({ progress }),
            setIsCookieConsentEnabled: (value) => set({ isCookieConsentEnabled: value }),
            setLoanAmount: (amount) => set({ loanAmount: amount }),
            setInstallmentCount: (count) => set({ installmentCount: count }),
            setInsuranceEnabled: (value) => set({ insuranceEnabled: value }),
            setSelectedPaymentPlan: (plan) => set({ selectedPaymentPlan: plan }),
            setPaymentData: (data) => set({ paymentData: data }),
            setContractData: (data) => set({ contractData: data }),
            setSelectedMplPlan: (plan) => set({ selectedMplPlan: plan }),
            setMplPaymentData: (data) => set({ mplPaymentData: data }),
            resetStore: () =>
                set({
                    isAuthenticated: false,
                    isExiting: false,
                    upsellFlow: false,
                    vendorToken: '',
                    phoneNumber: null,
                    progress: 0,
                    isCookieConsentEnabled: false,
                    loanAmount: null,
                    installmentCount: null,
                    insuranceEnabled: false,
                    selectedPaymentPlan: null,
                    paymentData: null,
                    contractData: null,
                    selectedMplPlan: null,
                    mplPaymentData: null,
                }),
        }),
        {
            name: 'leanpay-credit-session',
            storage: createJSONStorage(() => sessionStorage),
            partialize: (state) => {
                // Exclude isExiting from persistence - it's transient state
                const { isExiting: _, ...rest } = state
                return rest
            },
        },
    ),
)
